import React, { useCallback, useEffect, useMemo, useState } from "react";
import Pdf from "./Pdf";
import LoadingBoundary from "../ui_atoms/LoadingBoundary";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import { customAlphabet } from "nanoid";
import { AssetWaitingView } from "./AssetWaitingView";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import { ActionButton } from "../screens/user/ScreenHeader";
import { useAssetWaiting } from "../hooks/assets";
import Loading from "./Loading";
import VStack from "../ui_atoms/VStack";
import { Platform, StyleProp, useWindowDimensions, ViewStyle } from "react-native";
import { useTheme } from "../hooks/use-theme";

const generateShortId = customAlphabet("0123456789", 5);

type ScreenParams = StackScreenProps<RootStackParamList>;

type PdfViewerProps = {
    assetId?: string;
    assetUrl?: string;
    filename?: string;
    waitingDescription: string;
    style?: StyleProp<ViewStyle>;
    scrollEnabled?: boolean;
    /**
     * iOS-only: bottom content inset (points) for the native PDF scroll view, so the end of the
     * last page can be scrolled clear of an overlay (e.g. the translucent ScreenCard action bar)
     * while the PDF keeps filling the whole view.
     */
    contentInsetBottom?: number;
};

const PdfAssetViewer = ({
    assetId,
    assetUrl,
    filename,
    waitingDescription,
    style,
    scrollEnabled,
    contentInsetBottom,
}: PdfViewerProps) => {
    const { colors } = useTheme();
    const [isShareAvailable, setIsShareAvailable] = useState(false);
    const navigator = useNavigation<ScreenParams["navigation"]>();
    const [pdfError, setPdfError] = useState<string | null>();
    const [isSharing, setIsSharing] = useState(false);
    const { width: windowWidth } = useWindowDimensions();
    useEffect(() => {
        const getIsAvailableAsync = async () => {
            setIsShareAvailable(await Sharing.isAvailableAsync());
        };
        void getIsAvailableAsync();
    }, []);

    const assetIdData = useAssetWaiting(assetId);

    const { waiting, error, asset, reset } = useMemo(() => {
        if (!assetUrl) {
            return assetIdData;
        } else {
            return {
                waiting: false,
                error: null,
                asset: { url: assetUrl },
                reset: () => {},
            };
        }
    }, [assetIdData, assetUrl]);

    const sharePdf = useCallback(() => {
        const fn = async () => {
            if (asset && asset.url) {
                try {
                    setIsSharing(true);
                    if (Platform.OS === "web") {
                        // expo-file-system has no web backend, so the PDF cannot be downloaded
                        // locally first. The browser share sheet takes the remote URL as is.
                        try {
                            await Sharing.shareAsync(asset.url);
                        } catch (error) {
                            // Dismissing the share sheet rejects with AbortError, which is not
                            // a failure worth reporting.
                            if ((error as Error)?.name !== "AbortError") {
                                throw error;
                            }
                        }
                        return;
                    }
                    const randomFileId = generateShortId();
                    const localUri = new FileSystem.File(
                        FileSystem.Paths.document,
                        `${(filename || `file-${randomFileId}`).replaceAll(" ", "_")}.pdf`,
                    );
                    if (localUri.exists) {
                        localUri.delete();
                    }
                    const downloadRet = await FileSystem.File.downloadFileAsync(
                        asset.url || "",
                        localUri,
                    );
                    await Sharing.shareAsync(downloadRet.uri);
                } finally {
                    setIsSharing(false);
                }
            }
        };
        void fn();
    }, [asset, filename]);

    useEffect(() => {
        if (isShareAvailable && asset) {
            navigator.setOptions?.({
                headerRight: () => {
                    return <ActionButton icon="share" onPress={sharePdf} disabled={isSharing} />;
                },
            });
        } else {
            navigator.setOptions?.({
                headerRight: undefined,
            });
        }
    }, [asset, isShareAvailable, navigator, sharePdf, assetId, isSharing]);
    const pageSpacing = 10;

    return (
        <LoadingBoundary error={error || pdfError} clearError={reset}>
            {waiting ? (
                <VStack gap={1}>
                    <AssetWaitingView description={waitingDescription} />
                    <Loading />
                </VStack>
            ) : asset ? (
                <Pdf
                    fitPolicy={0}
                    spacing={pageSpacing}
                    trustAllCerts={false}
                    scrollEnabled={scrollEnabled}
                    contentInsetBottom={contentInsetBottom}
                    source={{
                        uri: asset.url || "",
                        cache: true,
                    }}
                    onError={(error) => {
                        let errorMessage: string;
                        if (error instanceof Error) {
                            errorMessage = error.message;
                        } else {
                            errorMessage = JSON.stringify(error);
                        }
                        setPdfError(errorMessage);
                    }}
                    style={[
                        {
                            flex: 1,
                            flexGrow: 1,
                            backgroundColor: colors.lighterGrey,
                            width: windowWidth,
                            // The native PDF viewer must own a bounded (screen-sized) viewport so
                            // its internal scroll view can scroll the document and pan the page
                            // after a pinch-zoom. Sizing it to the whole document height instead
                            // would leave that scroll view nothing to scroll; flex:1 fills the
                            // available space.
                            // flex:1 needs an ancestor with a resolved height — a form sheet has
                            // none by default, see PDF_SHEET_CONTENT in navigations/pdf-sheet.ts.
                            justifyContent: "flex-start",
                            overflow: "visible",
                        },
                        style,
                    ]}
                />
            ) : null}
        </LoadingBoundary>
    );
};

export default PdfAssetViewer;
